import type {
	Cell,
	Column,
	Header,
	HeaderGroup,
	Row,
	Table as TableReact,
} from '@tanstack/react-table'

import React from 'react'
import { flexRender } from '@tanstack/react-table'
import { findIndex, flatten, map, prop, propEq, sum } from 'lodash/fp'
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/shared/components/ui/table'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

export default function DataTable<T>({
	table,
	divideX,
	divideY,
	getRowClassName,
	getCellClassName,
	compact,
	className,
}: {
	className?: string
	table: TableReact<T>
	getRowClassName?: (row: Row<T>) => string
	getCellClassName?: (row: Row<T>, cell: Cell<T, unknown>) => string
	compact?: boolean
	divideX?: boolean
	divideY?: boolean
}) {
	const headerCellClassName = cn('text-xs', {
		'px-2 py-1': compact,
		'px-3 py-2': !compact,
		'border-r': divideX,
		'border-b': divideY,
	})
	const bodyCellClassName = cn({
		'px-2': compact,
		'px-3': !compact,
		'border-r': divideX,
		'border-b': divideY,
	})
	const tableHeaderGroups = table.getHeaderGroups()

	return (
		<div className={cn('relative w-full overflow-auto', className)}>
			<Table className='table-fixed'>
				<TableHeader>
					{table.getHeaderGroups().map((headerGroup) => (
						<TableRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => (
								<TableHead
									key={header.id}
									className={headerCellClassName}
									style={getCellStyle(
										header.column,
										getLeftOffset(header.id, tableHeaderGroups)
									)}
								>
									<div
										{...{
											className: header.column.getCanSort()
												? 'cursor-pointer select-none flex items-center'
												: '',
											onClick: header.column.getToggleSortingHandler(),
										}}
									>
										<Tooltip>
											<TooltipTrigger asChild>
												<span className='truncate'>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
												</span>
											</TooltipTrigger>
											<TooltipContent>
												{header.column.columnDef.header as string}
											</TooltipContent>
										</Tooltip>
										{{
											asc: (
												<ArrowUpIcon
													strokeWidth={3}
													className='size-3 flex-none'
												/>
											),
											desc: (
												<ArrowDownIcon
													strokeWidth={3}
													className='size-3 flex-none'
												/>
											),
										}[header.column.getIsSorted() as string] ?? null}
									</div>
									{header.column.getCanResize() && (
										<div
											{...{
												onDoubleClick: () => header.column.resetSize(),
												onMouseDown: header.getResizeHandler(),
												onTouchStart: header.getResizeHandler(),
												className: `resizer ${
													header.column.getIsResizing() ? 'isResizing' : ''
												}`,
												style: {
													transform: header.column.getIsResizing()
														? `translateX(${
																(table.options.columnResizeDirection === 'rtl'
																	? -1
																	: 1) *
																(table.getState().columnSizingInfo
																	.deltaOffset ?? 0)
															}px)`
														: '',
												},
											}}
										/>
									)}
								</TableHead>
							))}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{table.getRowModel().rows.map((row) => (
						<TableRow key={row.id} className={getRowClassName?.(row) || 'h-12'}>
							{row.getVisibleCells().map((cell) => (
								<TableCell
									key={cell.id}
									className={cn(
										bodyCellClassName,
										getCellClassName?.(row, cell)
									)}
									style={getCellStyle<T>(
										cell.column,
										getLeftOffset<T>(cell.column.id, tableHeaderGroups)
									)}
								>
									{flexRender(cell.column.columnDef.cell, cell.getContext())}
								</TableCell>
							))}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	)
}
function getCellStyle<T>(
	column: Column<T, unknown>,
	left: number
): React.CSSProperties {
	const width =
		column.getSize() === Number.MAX_SAFE_INTEGER ? 'auto' : column.getSize()
	if (column.getIsPinned() === 'left') {
		return {
			width,
			position: 'sticky',
			backgroundColor: 'inherit',
			zIndex: 1,
			left,
		}
	} else {
		return {
			width,
			position: 'relative',
		}
	}
}
function getLeftOffset<T>(
	id: string,
	tableHeaderGroups: HeaderGroup<T>[]
): number {
	const isHeaderPinned = (header: Header<T, unknown>) =>
		header.column.getIsPinned() === 'left'
	const headers = flatten(map(prop('headers'), tableHeaderGroups))
	const index = findIndex(propEq('id', id), headers)
	const firstPinnedIndex = findIndex(isHeaderPinned, headers)
	const widths = headers
		.slice(firstPinnedIndex, index)
		.filter(isHeaderPinned)
		.map((header) => header.getSize())

	return sum(widths)
}
